import type { ReactNode } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { ICON_COLOR } from '@zep-us/zus/tokens';
import { useViewport } from '@/hooks';
import { XIcon } from '@/icons';
import { Button, type ButtonProps, Chip, IconButton } from '@/ui_v3';
import { ResponsiveDialog } from '@/ui_v3/components/dialog';
import { cn } from '@/utils/tailwind.util';

/**
 * 디자인 시스템의 **Event Modal**(Figma 13798:21548).
 *
 * 좌측에서 왜 이걸 봐야 하는지 말하고, 우측 이미지가 그 말을 받치고, 하단 액션 바의
 * 버튼 하나로 끝난다. 2주 무료 이용권 안내처럼 **한 번에 하나를 권하는 화면**에 쓴다.
 *
 * `PromotionModal` 과 헷갈리기 쉬운데 다른 물건이다. 그쪽은 배너에서 이어지는
 * 프로모션 알림이라 데스크탑이 960 폭에 이미지가 오른쪽 끝까지 흘러나가고, 모바일에서는
 * 아예 하단 카드로 바뀐다. 이쪽은 시안대로 720 폭, 우측 이미지가 rounded 컨테이너 안에
 * 담기고, 액션이 본문과 선으로 분리된 바에 놓인다. 둘을 한 컴포넌트로 합치면 프롭으로
 * 갈라지는 분기가 레이아웃 전체에 퍼진다.
 */
export interface EventModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** 닫기 버튼의 현재 로케일 접근성 라벨. */
  closeLabel: string;
  /** 좌측 큰 제목. 줄바꿈은 호출부가 `<br />` 로 직접 잡는다(시안이 2줄 기준). */
  title: ReactNode;
  description: ReactNode;
  /** 우측 컨테이너를 채우는 이미지. 없으면 빈 회색 판이 남는다. */
  imageUrl: string;
  /** 제목 위 칩. 「New」처럼 짧은 한 마디만 들어간다. */
  badge?: {
    label: string;
    className?: string;
    textClassName?: string;
  };
  primaryLabel: string;
  primaryIntent?: ButtonProps['intent'];
  primaryClassName?: string;
  /** zus Button 의 `leftSlot` 이 Element 만 받는다(ReactNode 불가). */
  primaryLeftIcon?: React.JSX.Element;
  /** 라벨 뒤에 붙는 아이콘. 「…하러 가기」처럼 이동을 뜻할 때 쓴다(시안 1846:5569). */
  primaryRightIcon?: React.JSX.Element;
  onPrimaryClick: () => void;
  /**
   * 보조 버튼. 넘기지 않으면 기본 버튼이 바를 꽉 채운다.
   * 넘기면 시안대로 보조가 좁게(flex-1), 기본이 넓게(400) 나뉜다.
   */
  secondaryLabel?: string;
  onSecondaryClick?: () => void;
}

/**
 * 시안 우측 Image Container: 505 높이, rounded 24, 배경 bg/neutral.
 *
 * 아트워크가 컨테이너를 꽉 채우는 배경형이라 `cover` 로 덮는다. 어디를 기준으로 자를지는
 * 화면마다 다르다 — 데스크탑 시안(1846:5569)은 이미지를 위에 붙이고, 모바일
 * 시안(1925:4988)은 `translate(-50%,-50%)` 로 **가운데**에 놓는다. 모바일에서 위를
 * 기준으로 자르면 아래 서 있는 캐릭터의 다리가 잘려 나간다.
 */
const ImagePanel = ({
  imageUrl,
  className,
  objectPosition = 'object-top'
}: {
  imageUrl: string;
  className?: string;
  objectPosition?: 'object-top' | 'object-center';
}) => <div className={cn('overflow-hidden rounded-lg bg-neutral', 'flex items-center justify-center', className)} data-sentry-component="ImagePanel" data-sentry-source-file="EventModal.tsx">
    <img alt="" src={imageUrl} className={cn('size-full object-cover', objectPosition)} />
  </div>;
const ActionBar = ({
  primaryLabel,
  primaryIntent,
  primaryClassName,
  primaryLeftIcon,
  primaryRightIcon,
  onPrimaryClick,
  secondaryLabel,
  onSecondaryClick,
  isMobile
}: Pick<EventModalProps, 'primaryLabel' | 'primaryIntent' | 'primaryClassName' | 'primaryLeftIcon' | 'primaryRightIcon' | 'onPrimaryClick' | 'secondaryLabel' | 'onSecondaryClick'> & {
  isMobile: boolean;
}) => {
  const hasSecondary = Boolean(secondaryLabel && onSecondaryClick);
  // 시안은 모바일에서만 한 단계 낮춰 잡는다(1925:4988·1925:4871 = md, 데스크탑 = lg).
  // 좁은 폭에서 lg 는 버튼 두 개가 액션 바 높이를 밀어 올린다.
  const buttonSize = isMobile ? 'md' : 'lg';
  return (
    // 시안(13798:29254): 위쪽 경계선 + 아래로 번지는 그림자. 본문이 길어 스크롤될 때
    // 액션이 본문 위에 떠 있다는 걸 알리는 장치라, 경계선만 두면 밋밋해진다.
    <div className={cn('flex w-full shrink-0 flex-col items-center gap-md border-t border-alternative bg-default shadow-[0px_4px_6px_rgba(28,27,31,0.12),0px_0px_8px_rgba(28,27,31,0.04)]', isMobile ? 'p-md' : 'px-lg pb-lg pt-md')} data-sentry-component="ActionBar" data-sentry-source-file="EventModal.tsx">
      <div className="flex w-full items-start gap-xs">
        {hasSecondary &&
        // 데스크탑은 보조가 남는 폭(flex-1), 기본이 400. 모바일은 시안(1925:4878)이
        // 388 을 140 : 240 으로 나눈다 — 어느 쪽이든 반씩은 아니다. 반씩 주면
        // "다음에"가 업그레이드와 같은 무게로 보인다. 미루는 쪽이 더 쉬워 보이면 안 된다.
        <Button variant="contained" intent="neutral" size={buttonSize} className={cn('min-w-[48px]', isMobile ? 'flex-[140]' : 'flex-1')} onClick={onSecondaryClick}>
            {secondaryLabel}
          </Button>}
        <Button intent={primaryIntent} size={buttonSize} onClick={onPrimaryClick} leftSlot={primaryLeftIcon} rightSlot={primaryRightIcon} className={cn(hasSecondary ? isMobile ? 'flex-[240]' : 'w-[400px] max-w-[400px] shrink-0' : 'flex-1', primaryClassName)} data-sentry-element="Button" data-sentry-source-file="EventModal.tsx">
          {primaryLabel}
        </Button>
      </div>
    </div>
  );
};
export const EventModal = ({
  isOpen,
  onClose,
  closeLabel,
  title,
  description,
  imageUrl,
  badge,
  primaryLabel,
  primaryIntent = 'strong',
  primaryClassName,
  primaryLeftIcon,
  primaryRightIcon,
  onPrimaryClick,
  secondaryLabel,
  onSecondaryClick
}: EventModalProps) => {
  const {
    isMobile
  } = useViewport();
  if (!isOpen) return null;
  const actionBar = <ActionBar primaryLabel={primaryLabel} primaryIntent={primaryIntent} primaryClassName={primaryClassName} primaryLeftIcon={primaryLeftIcon} primaryRightIcon={primaryRightIcon} onPrimaryClick={onPrimaryClick} secondaryLabel={secondaryLabel} onSecondaryClick={onSecondaryClick} isMobile={isMobile} />;

  // 시안(1811:86017): purple-50 배경 / purple-500 글자 / pill.
  const badgeChip = badge ? <Chip size="md" rounded="full" className={cn('border-opacity-assistive bg-purple-50', badge.className)}>
      <span className={cn('text-body-sm-semibold text-purple-500', badge.textClassName)}>
        {badge.label}
      </span>
    </Chip> : null;

  // 플랜 시작 안내와 동일하게 모바일에서는 바텀시트가 본문 스크롤과 액션 바를
  // 나눠 가진다. 기존 중앙 다이얼로그의 `h-fit + flex-1` 조합은 작은 화면에서
  // 본문 높이를 계산하지 못해 헤더와 이미지가 시트 밖으로 밀려났다.
  if (isMobile) {
    return <ResponsiveDialog size="lg" forceDrawerOnMobile
    /**
     * 높이는 **내용이 정한다.** 시안(1925:4988)의 시트는 헤더 + 설명 + 460 아트 +
     * 액션 바를 합한 만큼만 차지한다.
     *
     * 90dvh 를 `h-` 로 고정했더니 큰 화면에서 남는 높이가 아트 아래 흰 띠로 남았다.
     * 상한으로만 걸어 두면 낮은 화면에서는 본문이 스크롤되고 액션 바는 그대로 붙는다.
     */ className="h-auto max-h-[calc(90dvh-env(safe-area-inset-top))]" onClose={onClose}>
        <div className="flex h-full min-h-0 flex-col">
          {/**
           * 시안(1925:4988)의 Dialog/Header — 칩과 제목이 **닫기 버튼과 같은 줄**에
           * 산다. 데스크탑처럼 닫기를 absolute 로 띄우면 제목이 그 아래를 지나가면서
           * 두 줄짜리 제목의 끝이 버튼에 가린다.
           */}
          <header className="flex w-full shrink-0 items-start gap-md px-md pb-xs pt-md">
            <div className="flex min-h-[36px] min-w-0 flex-1 flex-col items-start gap-sm">
              {badgeChip}
              <h1 className="w-full whitespace-pre-line text-heading-md-bold text-default">
                {title}
              </h1>
            </div>
            <IconButton variant="transparent" intent="neutral" size="sm" aria-label={closeLabel} onClick={onClose}>
              <XIcon fill={ICON_COLOR.neutral} size="md" />
            </IconButton>
          </header>
          <main className="flex min-h-0 flex-1 flex-col gap-sm overflow-y-auto px-md pb-md">
            <p className="whitespace-pre-line text-body-md-regular text-default">
              {description}
            </p>
            {/**
             * 시안은 460 고정이다. 240 으로 눌러 두었더니 아트가 카드 안에서 작아지고
             * 그만큼 아래에 흰 여백만 남았다. 화면이 낮으면 본문이 스크롤되고 하단
             * 액션 바는 그대로 고정된다.
             */}
            <ImagePanel imageUrl={imageUrl} className="h-[460px] w-full shrink-0" objectPosition="object-center" />
          </main>
          {actionBar}
        </div>
      </ResponsiveDialog>;
  }
  return <DialogPrimitive.Root open={isOpen} onOpenChange={open => {
    if (!open) onClose();
  }} data-sentry-element="unknown" data-sentry-component="EventModal" data-sentry-source-file="EventModal.tsx">
      <DialogPrimitive.Portal data-sentry-element="unknown" data-sentry-source-file="EventModal.tsx">
        {/* dim·페이드는 ZUS DialogOverlay 와 같은 값으로 맞춘다. */}
        <DialogPrimitive.Overlay className={cn('fixed inset-0 z-modal bg-[rgba(28,27,31,0.24)] transition-all duration-200', 'data-[state=open]:animate-in data-[state=open]:fade-in-0', 'data-[state=closed]:animate-out data-[state=closed]:fade-out-0')} data-sentry-element="unknown" data-sentry-source-file="EventModal.tsx" />
        <DialogPrimitive.Content aria-describedby={undefined} className={cn(
      // ⚠️ `left-1/2 top-1/2 -translate-1/2` 로 가운데를 잡지 않는다. tailwindcss-animate
      // 의 zoom/fade 키프레임이 `transform` 을 통째로 다시 쓰면서 그 -50% 이동을
      // 지워버려, 모달이 좌상단에서 대각선으로 날아온다.
      // inset-0 + margin auto 는 transform 을 안 쓰므로 애니메이션과 안 싸운다.
      // 폭은 아래에서 명시하므로 여기선 높이만 내용에 맞춘다(w-fit 을 같이 걸면
      // 그 폭 지정과 충돌한다).
      'fixed inset-0 z-modal m-auto h-fit', 'flex flex-col overflow-hidden rounded-lg bg-default', 'shadow-[0px_8px_20px_0px_rgba(28,27,31,0.12),0px_0px_16px_0px_rgba(28,27,31,0.08)]', 'duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out', 'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0', 'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
      // 시안 데스크탑 760 × 685. 창이 좁아지면 따라 줄어야 하므로 폭은 상한으로 건다.
      'w-[calc(100vw-32px)] mobile-lg:w-[760px]', 'max-h-[calc(100dvh-48px)]')} data-sentry-element="unknown" data-sentry-source-file="EventModal.tsx">
          <div className="flex min-h-0 flex-1 items-stretch justify-between gap-lg px-lg pt-lg">
            {/* 시안 Plan title: 272 상한, 내부 패딩 16. */}
            <div className="flex max-w-[272px] flex-1 flex-col gap-md overflow-y-auto px-md pt-md">
              <div className="flex flex-col items-start gap-lg">
                {badgeChip}
                {/* 시안은 opacity 90 이 걸려 있다. 색 토큰을 바꾸는 대신 그대로 둔다. */}
                <DialogPrimitive.Title className="whitespace-pre-line text-heading-xl-extrabold text-default opacity-90" data-sentry-element="unknown" data-sentry-source-file="EventModal.tsx">
                  {title}
                </DialogPrimitive.Title>
              </div>
              <p className="whitespace-pre-line text-body-lg-medium text-default opacity-90">
                {description}
              </p>
            </div>
            {/* 시안은 505 고정인데, 낮은 창에서 그대로 두면 액션 바가 잘린다.
                  상한으로 걸어 남는 높이만 차지하게 한다. */}
            <ImagePanel imageUrl={imageUrl} className="h-full max-h-[505px] flex-1" data-sentry-element="ImagePanel" data-sentry-source-file="EventModal.tsx" />
          </div>

          {actionBar}

          <Button intent="neutral" rounded="full" size="2xs" variant="contained" aria-label={closeLabel} leftSlot={<XIcon fill={ICON_COLOR.neutral} size="md" />} className="absolute right-[16px] top-[16px] size-[36px] p-0" onClick={onClose} data-sentry-element="Button" data-sentry-source-file="EventModal.tsx" />
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>;
};